import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  KeyRound,
  LayoutDashboard,
  ListOrdered,
  SearchCode,
  Settings,
  TrendingUp,
  UsersRound,
  WalletCards
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import { PATHS } from '../../../router/paths.ts';
import { SidebarShell } from '../app-shell/sidebar-shell.tsx';
import { NavItemRow, type NavItemDef } from '../app-shell/nav-item-row.tsx';
import { UserRowFooter } from '../app-shell/user-row-footer.tsx';
import { OrgSwitcher } from '@components/back-office/org-switcher/org-switcher.tsx';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { useGetZapfeedPosts } from '@utils/api/zapfeed/use-get-zapfeed-posts';

export default function SideNavBackOffice() {
  const { t } = useTranslation();
  const { getColour, getBrandTint, theme } = useThemeContext();
  const { isSysOp } = useRoles();
  const location = useLocation();
  const { data: zapfeedData } = useGetZapfeedPosts();

  const isOnOperationsGroup =
    location.pathname.startsWith(PATHS().BO_OPERATIONS) ||
    location.pathname === PATHS().BO_SESSION_INSPECTOR ||
    location.pathname.startsWith(PATHS().PRIORITY_QUEUE);
  const [showOperationsSubItems, setShowOperationsSubItems] =
    useState(isOnOperationsGroup);

  useEffect(() => {
    if (isOnOperationsGroup) setShowOperationsSubItems(true);
  }, [isOnOperationsGroup]);

  const backLinkStyle = useMemo(
    () =>
      StyleSheet.create({
        link: {
          color: getColour('inactiveText'),
          transition: 'background-color 0.15s ease, color 0.15s ease',
          ':hover': {
            color: getColour('activeText'),
            backgroundColor: getBrandTint(0.06)
          }
        }
      }),
    [getColour, getBrandTint, theme]
  );

  const navItems = useMemo<NavItemDef[]>(
    () => [
      {
        name: t('dashboard'),
        href: PATHS().BO_DASHBOARD,
        icon: LayoutDashboard,
        badge: zapfeedData?.openCount || undefined
      },
      { name: t('customers'), href: PATHS().CUSTOMERS, icon: UsersRound },
      { name: t('plans'), href: PATHS().PLANS, icon: WalletCards },
      {
        name: t('exceedances'),
        href: PATHS().BO_EXCEEDANCES,
        icon: AlertTriangle
      },
      {
        name: t('usage-and-revenue'),
        href: PATHS().BO_USAGE_REVENUE,
        icon: TrendingUp
      },
      {
        name: t('operations'),
        href: PATHS().BO_OPERATIONS,
        icon: Activity,
        children: [
          {
            name: t('session-inspector'),
            href: PATHS().BO_SESSION_INSPECTOR,
            icon: SearchCode
          },
          {
            name: t('priority-queue'),
            href: PATHS().PRIORITY_QUEUE,
            icon: ListOrdered
          }
        ]
      },
      { name: t('licensing'), href: PATHS().BO_LICENSING, icon: KeyRound },
      { name: t('settings'), href: PATHS().BO_SETTINGS, icon: Settings }
    ],
    [t, zapfeedData?.openCount]
  );

  return (
    <SidebarShell
      navSlot={
        <>
          <div
            className="mb-2 pb-2"
            style={{ borderBottom: `1px solid ${getColour('hairline')}` }}
          >
            <NavLink
              to={PATHS().HOME}
              className={cn(
                css(backLinkStyle.link),
                'flex items-center gap-2 rounded-2xl px-4 py-2 text-xs font-medium'
              )}
            >
              <ArrowLeft size={13} className="shrink-0" />
              {t('workspace')}
            </NavLink>
          </div>
          <ul role="list" className="m-0 p-0 space-y-0.5">
            {navItems.map(item => (
              <NavItemRow
                key={item.href}
                item={item}
                expanded={item.children ? showOperationsSubItems : undefined}
                onToggleExpand={
                  item.children
                    ? () => setShowOperationsSubItems(v => !v)
                    : undefined
                }
              />
            ))}
          </ul>
        </>
      }
      footerSlot={
        <>
          {isSysOp && <OrgSwitcher />}
          <UserRowFooter />
        </>
      }
    />
  );
}
